import { LinkNode } from '@lexical/link'
import { ListNode, ListItemNode } from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode } from '@lexical/rich-text'
import type { SerializedEditorState } from 'lexical'

import { isInDummyMode } from 'dummy/static'
import { isHtml } from 'utils/CommonUtils'

import CustomLinkPlugin from './custom-link-plugin'
import DisableLinksPlugin from './disable-links-plugin'
import HtmlImportPlugin from './html-import-plugin'

type Props = {
  content: SerializedEditorState | string
  className?: string
}

// Same theme as your editor for consistency
// TODO: this needs to come from shared place
const theme = {
  paragraph: 'mb-[0.5em] last:mb-0',
  list: {
    nested: { listitem: 'ml-4' },
    ol: 'list-decimal pl-6',
    ul: 'list-disc pl-6',
    listitem: 'mb-1'
  },
  link: 'underline [text-underline-position:under] cursor-pointer focus-visible:ring focus-visible:ring-offset-2 rounded-sm',
  text: {
    bold: 'font-bold',
    italic: 'italic'
  }
}

const initialConfig = {
  namespace: 'content-block-ecp-readonly',
  theme,
  editable: false, // Read-only mode
  onError: (error: Error) => {
    console.error('Lexical renderer error:', error)
  }
}

const LexicalRenderer = ({ content, className = '' }: Props) => {
  if (!content) {
    return null
  }

  const contentString = content as string
  const contentIsHtml = isHtml(contentString)
  const editorStateString = contentIsHtml ? null : contentString

  return (
    <div className={className}>
      <LexicalComposer
        initialConfig={{
          ...initialConfig,
          nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
          editorState: editorStateString
        }}
        key={contentString}
      >
        <RichTextPlugin
          ErrorBoundary={LexicalErrorBoundary}
          contentEditable={
            <ContentEditable
              aria-autocomplete={undefined}
              aria-readonly={undefined} // Override read-only mode, as its wrong
              className="outline-none focus:outline-none"
            />
          }
        />

        {contentIsHtml && <HtmlImportPlugin html={contentString} />}

        {isInDummyMode() ? (
          <DisableLinksPlugin disabled={true} />
        ) : (
          <CustomLinkPlugin />
        )}
      </LexicalComposer>
    </div>
  )
}

export default LexicalRenderer
